import { ReactElement } from 'react';
import * as Sentry from '@sentry/nextjs';
import { atom } from 'jotai';
import { atomWithReset, RESET } from 'jotai/utils';
import SmsVerificationPhoneNumberStep from 'src/components/AzarOnly/Login/Steps/SmsVerification/PhoneNumberStep';
import SmsVerificationVerifyCodeStep from 'src/components/AzarOnly/Login/Steps/SmsVerification/VerifyCodeStep';
import { loginWithPhoneNumberAtom, showInduceRegisterModalAtom } from 'src/stores/auth/atoms';
import { kmcStateAtom, requestOpenKmcPopupAtom } from 'src/stores/kmc/atoms';
import { smsPreVerificationMethodAtom, featureSettingParamsFromOptionalConsentsAtom, LoginModalType, loginModalTypeAtom, pushSocialRegisterHistoryAtom, resetRegisterAtom, signUpSettingsLoadableAtom, socialKmcVerificationAtom } from 'src/stores/register/atoms';
import { pushSocialLoginHistoryAtom } from 'src/stores/socialLogin/atom';
import { showToastAtom } from 'src/stores/toast/atoms';
import { ToastType } from 'src/types/Toast';
import { ErrorAlertMessage, ErrorResponse } from 'src/utils/api';
import getDeviceId from 'src/utils/device/id';
import { isAxiosError } from 'src/utils/error';
import { matchEmailAPI } from './apis';
import { DeviceTransferMatchEmailErrorCode, deviceTransferMatchEmailErrorCodeTranslationMap, VerifyAccountResponse, VerifyPreparation } from './types';
export const phoneNumberLoginHistoryAtom = atomWithReset<JSX.Element[]>([]);
export const pushPhoneNumberLoginHistoryAtom = atom(null, (get, set, nextStep: ReactElement) => {
  set(phoneNumberLoginHistoryAtom, history => [...history, nextStep]);
});
export const popPhoneNumberLoginHistoryAtop = atom(null, (get, set) => {
  set(phoneNumberLoginHistoryAtom, history => history.slice(0, -1));
});
export const errorMessageAtom = atom<undefined | string>('');
const initSmsPhoneNumberValue = {
  phone: '',
  verifyPreparation: {
    countryName: undefined,
    countryPrefixNumber: undefined
  },
  errorMessage: ''
};
export const smsPhoneNumberAtom = atomWithReset<{
  phone: string;
  verifyPreparation: VerifyPreparation;
  errorMessage?: string;
}>(initSmsPhoneNumberValue);
const initSmsVerificationValue = {
  verifyCode: '',
  formattedPhoneNumber: '',
  errorMessage: ''
};
export const smsVerificationAtom = atomWithReset<{
  verifyCode: string;
  formattedPhoneNumber: string;
  errorMessage?: string;
  isError?: boolean;
}>(initSmsVerificationValue);
export const nextSmsVerificationStepAtom = atom(null, (get, set) => {
  const loginModalType = get(loginModalTypeAtom);
  switch (loginModalType) {
    case LoginModalType.SOCIAL_REGISTER:
      set(pushSocialRegisterHistoryAtom, <SmsVerificationVerifyCodeStep />);
      break;
    case LoginModalType.SOCIAL_LOGIN:
      {
        set(pushSocialLoginHistoryAtom, <SmsVerificationVerifyCodeStep />);
        break;
      }
    case LoginModalType.PHONE_NUMBER_LOGIN:
      {
        set(pushPhoneNumberLoginHistoryAtom, <SmsVerificationVerifyCodeStep />);
        break;
      }
  }
});
export const smsVerificationResponseAtom = atom<VerifyAccountResponse | null>(null);
export const openPhoneKmcPopupAtom = atom(null, (_get, set) => {
  set(requestOpenKmcPopupAtom, 'phone');
});
let cleanupPendingLoginErrorToast: (() => void) | null = null;
const showLoginErrorToastWhenFocusedAtom = atom(null, (_get, set) => {
  const showToast = () => {
    cleanupPendingLoginErrorToast?.();
    cleanupPendingLoginErrorToast = null;
    set(showToastAtom, {
      message: 'LOGIN__ERROR',
      type: ToastType.ERROR
    });
  };
  if (typeof window === 'undefined' || typeof document === 'undefined' || document.visibilityState === 'visible' && document.hasFocus()) {
    showToast();
    return;
  }
  cleanupPendingLoginErrorToast?.();
  const handleWindowActivated = () => {
    if (document.visibilityState === 'visible' && document.hasFocus()) {
      showToast();
    }
  };
  const cleanup = () => {
    document.removeEventListener('visibilitychange', handleWindowActivated);
    window.removeEventListener('focus', handleWindowActivated);
  };
  cleanupPendingLoginErrorToast = cleanup;
  document.addEventListener('visibilitychange', handleWindowActivated);
  window.addEventListener('focus', handleWindowActivated);
});

// 서버 정책상 subSms는 TSP만 내려온다. phone/social 모두 설정값을 읽고, 하드코딩하지 않는다.
export const fallbackToTSPAtom = atom(null, (get, set) => {
  const loginModalType = get(loginModalTypeAtom);
  const signUpSettingsLoadable = get(signUpSettingsLoadableAtom);
  if (signUpSettingsLoadable.state !== 'hasData') {
    throw new Error('signup-settings is required for KMC fallback');
  }
  const fallbackMethod = signUpSettingsLoadable.data.smsVerifiedSettings?.subSms;
  if (!fallbackMethod) {
    throw new Error('subSms is required for KMC fallback');
  }
  set(kmcStateAtom, RESET);
  set(smsPreVerificationMethodAtom, fallbackMethod);
  if (loginModalType === LoginModalType.SOCIAL_REGISTER) {
    // SOCIAL_REGISTER를 유지한 채 SMS 인증 step으로 전환한다.
    // 이전 시도의 잔여 phone/KMC 인증 상태를 지우고 깨끗한 step으로 진입한다.
    set(socialKmcVerificationAtom, RESET);
    set(smsPhoneNumberAtom, RESET);
    set(smsVerificationAtom, RESET);
    set(pushSocialRegisterHistoryAtom, <SmsVerificationPhoneNumberStep />);
  } else {
    set(loginModalTypeAtom, LoginModalType.PHONE_NUMBER_LOGIN);
    set(pushPhoneNumberLoginHistoryAtom, <SmsVerificationPhoneNumberStep />);
  }
  set(showLoginErrorToastWhenFocusedAtom);
});
const initEmailVerificationValue = {
  email: ''
};
export const emailVerificationAtom = atomWithReset(initEmailVerificationValue);
const initPhoneNumberLoginEmailValue = {
  email: '',
  errorMessage: ''
};
export const phoneNumberLoginEmailAtom = atom(initPhoneNumberLoginEmailValue);
const resetLoginAtom = atom(null, (_get, set) => {
  set(smsPreVerificationMethodAtom, RESET);
  set(smsPhoneNumberAtom, initSmsPhoneNumberValue);
  set(smsVerificationAtom, initSmsVerificationValue);
  set(phoneNumberLoginEmailAtom, initPhoneNumberLoginEmailValue);
  set(phoneNumberLoginHistoryAtom, RESET);
  set(emailVerificationAtom, RESET);
});
export const isRemoveOldDeviceUserDetailsAtom = atom(false);
export const verifyErrorMessages: {
  [key: string]: string;
} = {
  VERIFICATION_FAILED: 'SIGNUP_PHONE_VERIFICATION_FAIL'
};
export const loginAfterMatchingEmailAtom = atom(null, async (get, set) => {
  const smsVerificationResponse = get(smsVerificationResponseAtom);
  if (!smsVerificationResponse) {
    Sentry.captureMessage('loginAfterMatchingEmailAtom called without smsVerificationResponse');
    return;
  }
  const {
    phoneId,
    phoneToken
  } = smsVerificationResponse;
  try {
    await matchEmailAPI({
      email: get(phoneNumberLoginEmailAtom).email,
      phoneId,
      phoneToken,
      deviceId: getDeviceId()
    });
    await set(loginWithPhoneNumberAtom);
    set(featureSettingParamsFromOptionalConsentsAtom, RESET);
  } catch (error) {
    if (!isAxiosError<ErrorResponse<ErrorAlertMessage>>(error)) {
      Sentry.captureException(error);
      return;
    }
    const {
      response
    } = error;
    set(phoneNumberLoginEmailAtom, {
      ...get(phoneNumberLoginEmailAtom),
      errorMessage: deviceTransferMatchEmailErrorCodeTranslationMap[response?.data.error.code as DeviceTransferMatchEmailErrorCode] || deviceTransferMatchEmailErrorCodeTranslationMap.VERIFICATION_FAILED
    });
  }
});
export const loginModalHeaderAtom = atom<{
  component?: ReactElement;
  onBack?: () => void;
} | void>(undefined);
export const returnLoginEntryAtom = atom(null, (_get, set) => {
  set(loginModalTypeAtom, LoginModalType.LOGIN_ENTRY);
  set(resetLoginAtom);
});

// KMC 자체 에러가 아니면 TSP fallback 대신 로그인 첫 화면으로 복귀하고 에러 토스트를 띄운다.
export const handleNonKmcSelfErrorAtom = atom(null, (_get, set) => {
  set(kmcStateAtom, RESET);
  set(resetRegisterAtom);
  set(returnLoginEntryAtom);
  set(showLoginErrorToastWhenFocusedAtom);
});
export const exitLoginModalAtom = atom(null, (get, set) => {
  set(returnLoginEntryAtom);

  // 가입 단계에서 이탈 시 2초 후 Bounce Rate 모달 노출
  set(showInduceRegisterModalAtom);
});